import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import '../style/AdminStaffAvailability.css';

// Matches RAD_MODALITIES / LAB_MACHINES in
// radiology-information-system-backend/routes/scheduling.js.
const EQUIPMENT_GROUPS = {
  modality: { label: 'Radiology Modality', options: ['CT', 'MRI', 'X-Ray', 'US', 'DEXA'] },
  lab_machine: { label: 'Lab Machine', options: ['Piccolo', 'DxH520', 'AIA360', 'AIA900', 'McKesson120'] },
};

const HOUR_OPTIONS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '17:40'];

const formatHourLabel = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
};

const toLocalDateString = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const getMonthDateRange = (date) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: endOfMonth.toISOString().split('T')[0],
  };
};

// Rendered by components/StaffAvailability.js at /availability (Machine
// Closures tab). Unlike Rad Tech / Lab Machine Availability, this tracks the
// EQUIPMENT's own state — down for maintenance, broken, a scheduled service
// call — independent of which staff are on the clock. It reads/writes
// equipment_blocked_times, which isModalityAvailableOnDate() and
// hasStaffForMachinesOnDate() (routes/scheduling.js) both check, so a closure
// logged here blocks booking that modality/machine the same way a staffing
// gap does, and is admin-only since it isn't a personal calendar.
export default function AdminMachineClosures({ BASE_URL, currentUser, user }) {
  const isAdmin = currentUser?.type === 'admin';

  const [equipmentType, setEquipmentType] = useState('modality');
  const [equipmentName, setEquipmentName] = useState(EQUIPMENT_GROUPS.modality.options[0]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blocks, setBlocks] = useState([]); // equipment_blocked_times rows for the month

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);
  const [fullDay, setFullDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');

  const fetchBlocks = useCallback(async () => {
    const { startDate, endDate } = getMonthDateRange(currentDate);
    try {
      const response = await fetch(
        `${BASE_URL}/scheduling/equipmentBlockedTimes?equipment_type=${equipmentType}&equipment_name=${encodeURIComponent(equipmentName)}&startDate=${startDate}&endDate=${endDate}`
      );
      if (response.ok) {
        const data = await response.json();
        setBlocks(data);
      }
    } catch (err) {
      logger.error('Error fetching equipment blocked times:', err);
    }
  }, [BASE_URL, currentDate, equipmentType, equipmentName]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const handleTypeChange = (type) => {
    setEquipmentType(type);
    setEquipmentName(EQUIPMENT_GROUPS[type].options[0]);
  };

  const handleDayClick = (day) => {
    if (!isAdmin) {
      toast.info('Only an admin can log a machine closure.');
      return;
    }
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    setPendingDate(dateStr);
    setFullDay(true);
    setStartTime('09:00');
    setEndTime('17:00');
    setReason('');
    setShowBlockModal(true);
  };

  const handleAddBlock = async () => {
    if (!fullDay && endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/scheduling/equipmentBlockedTimes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          equipment_type: equipmentType,
          equipment_name: equipmentName,
          blocked_date: pendingDate,
          full_day: fullDay,
          start_time: fullDay ? null : `${startTime}:00`,
          end_time: fullDay ? null : `${endTime}:00`,
          reason,
          user,
          type: currentUser?.type,
        }),
      });
      if (response.ok) {
        toast.success(`${equipmentName} closed on ${pendingDate}${fullDay ? ' (all day)' : ` from ${formatHourLabel(startTime)} to ${formatHourLabel(endTime)}`}`);
        setFullDay(true);
        setStartTime('09:00');
        setEndTime('17:00');
        setReason('');
        await fetchBlocks();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to log closure');
      }
    } catch (err) {
      logger.error('Error logging equipment closure:', err);
      toast.error('Error logging equipment closure');
    }
  };

  const handleDeleteBlock = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/equipmentBlockedTimes?id=${id}&user=${user}&type=${currentUser?.type}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Closure removed');
        await fetchBlocks();
      } else {
        toast.error('Failed to remove closure');
      }
    } catch (err) {
      logger.error('Error removing equipment closure:', err);
      toast.error('Error removing closure');
    }
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="avail-calendar-day" style={{ visibility: 'hidden' }} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayBlocks = blocks.filter(b => b.blocked_date.split('T')[0] === dateStr);
    const isFullyBlocked = dayBlocks.some(b => !!b.full_day);
    const hasPartialBlock = dayBlocks.some(b => !b.full_day);
    const isPast = new Date(dateStr) < new Date(new Date().toDateString());
    days.push(
      <div
        key={day}
        className={`avail-calendar-day ${isFullyBlocked ? 'full-blocked' : (hasPartialBlock ? 'partial-blocked' : '')} ${isPast ? 'past' : ''}`}
        onClick={() => handleDayClick(day)}
        title={isFullyBlocked ? 'Closed all day' : (hasPartialBlock ? 'Partially closed' : (isAdmin ? 'Click to log a closure' : 'Open'))}
      >
        <span className="avail-day-number">{day}</span>
        {isFullyBlocked && <span className="avail-day-badge full">Closed</span>}
        {!isFullyBlocked && hasPartialBlock && <span className="avail-day-badge partial">Limited</span>}
      </div>
    );
  }

  const upcomingBlocks = blocks
    .filter(b => new Date(b.blocked_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.blocked_date) - new Date(b.blocked_date) || a.start_time.localeCompare(b.start_time));

  const blocksForPendingDate = pendingDate ? blocks.filter(b => b.blocked_date.split('T')[0] === pendingDate) : [];

  return (
    <div className="avail-container">
      <h1 className="avail-title">Machine Closures</h1>
      <p className="avail-subtitle">
        Log a radiology modality or lab machine as closed — down for maintenance, a service call, or otherwise unusable —
        for a full day or a specific window. This blocks booking that equipment even if staff who run it are otherwise
        free, and is reflected on the Schedule calendar.
      </p>

      <div className="avail-selector-row">
        <label htmlFor="equipment-type-select">Equipment Type:</label>
        <select
          id="equipment-type-select"
          className="avail-selector"
          value={equipmentType}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {Object.entries(EQUIPMENT_GROUPS).map(([key, group]) => (
            <option key={key} value={key}>{group.label}</option>
          ))}
        </select>

        <label htmlFor="equipment-name-select">Equipment:</label>
        <select
          id="equipment-name-select"
          className="avail-selector"
          value={equipmentName}
          onChange={(e) => setEquipmentName(e.target.value)}
        >
          {EQUIPMENT_GROUPS[equipmentType].options.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {!isAdmin && (
        <p className="avail-subtitle">
          Machine closures are equipment-state records — only an admin can log or remove one.
        </p>
      )}

      <div className="avail-calendar-header">
        <button className="avail-nav-btn" onClick={prevMonth}>{'<'}</button>
        <h2>{currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}</h2>
        <button className="avail-nav-btn" onClick={nextMonth}>{'>'}</button>
      </div>

      <div className="avail-calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="avail-calendar-day-header">{d}</div>
        ))}
        {days}
      </div>

      {upcomingBlocks.length > 0 && (
        <div className="avail-upcoming-section">
          <h3>Upcoming Closures</h3>
          <ul className="avail-upcoming-list">
            {upcomingBlocks.map(b => {
              const [year, month, day] = b.blocked_date.split('T')[0].split('-');
              const display = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))
                .toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
              return (
                <li key={b.id} className={`avail-upcoming-item ${b.full_day ? '' : 'partial'}`}>
                  <span className="avail-upcoming-date">
                    {display}{!b.full_day && ` — ${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                    {!!b.full_day && ' — All Day'}
                  </span>
                  {b.reason && <span className="avail-upcoming-reason">{b.reason}</span>}
                  {isAdmin && (
                    <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showBlockModal && (
        <div className="avail-modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="avail-modal-box" onClick={e => e.stopPropagation()}>
            <h3>Close {equipmentName} on {pendingDate}</h3>

            {blocksForPendingDate.length > 0 && (
              <div className="avail-existing-blocks">
                <div className="avail-existing-blocks-title">Existing closures for this day</div>
                {blocksForPendingDate.map(b => (
                  <div key={b.id} className="avail-existing-block-item">
                    <span>
                      {b.full_day ? 'All Day' : `${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                      {b.reason ? ` (${b.reason})` : ''}
                    </span>
                    <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            <label className="avail-form-checkbox-label">
              <input type="checkbox" checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />
              Closed all day
            </label>

            {!fullDay && (
              <div className="avail-time-row">
                <label className="avail-form-label">
                  Start Time
                  <select className="avail-select" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
                <label className="avail-form-label">
                  End Time
                  <select className="avail-select" value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
              </div>
            )}

            <label className="avail-form-label">
              Reason (optional)
              <input
                type="text"
                className="avail-reason-input"
                placeholder="e.g. Maintenance, Service call, Broken..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>

            <div className="avail-modal-buttons">
              <button className="avail-btn-confirm" onClick={handleAddBlock}>Log Closure</button>
              <button className="avail-btn-cancel" onClick={() => setShowBlockModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
